import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, FileText, Euro, Package, TrendingUp, AlertTriangle, Shield } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article1 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-blue-600 text-white mr-4">
              <TrendingUp className="h-3 w-3 mr-1" />
              Market Analysis
            </Badge>
            <span className="text-sm text-gray-600">Ti Insight</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>5 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.1.title')}
          </h1>
          
          <img 
            src="https://ti-insight.com/wp-content/uploads/2016/10/European-Road-Freight-2016-report-iStock_31281272_LARGE-285x231.jpg" 
            alt="European Road Freight"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.1.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.1.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.1.p1')}
            </p>

            <p className="mb-6">
              {t('article.1.p2')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-100">
              <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                <TrendingUp className="mr-2" />
                {t('article.1.highlight.title')}
              </h3>
              <p className="text-sm text-blue-800">
                {t('article.1.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.1.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.1.p3')}
            </p>

            <p className="mb-6">
              {t('article.1.p4')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-green-900 mb-4">{language === 'ko' ? '연료 및 디젤 전망' : 'Fuel and Diesel Outlook'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-green-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'EU Fit for 55 영향' : 'EU Fit for 55 Impact'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '새로운 과세:' : 'New Taxation:'}</strong> {language === 'ko' ? '기후 영향 기반 과세' : 'Climate impact-based taxation'}</li>
                    <li><strong>{language === 'ko' ? '바이오연료:' : 'Biofuels:'}</strong> {language === 'ko' ? '프랑스, 포르투갈, 아일랜드 세금 혜택' : 'Tax benefits in France, Portugal, Ireland'}</li>
                    <li><strong>{language === 'ko' ? '스웨덴:' : 'Sweden:'}</strong> {language === 'ko' ? '바이오연료 완전 면세' : 'Complete biofuel tax exemption'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <AlertTriangle className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '디젤 가격 동향' : 'Diesel Price Trends'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '지난달 대비:' : 'vs Last Month:'}</strong> {language === 'ko' ? '+2.2% 상승' : '+2.2% increase'}</li>
                    <li><strong>{language === 'ko' ? '분기 대비:' : 'vs Quarter:'}</strong> {language === 'ko' ? '-0.81% 하락' : '-0.81% decline'}</li>
                    <li><strong>{language === 'ko' ? '전년 대비:' : 'vs Last Year:'}</strong> {language === 'ko' ? '-3.49% 하락' : '-3.49% decline'}</li>
                    <li><strong>{language === 'ko' ? '2026 예측:' : '2026 Forecast:'}</strong> {language === 'ko' ? '배럴당 $94 평균' : '$94/barrel average'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.1.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.1.p5')}
            </p>

            <p className="mb-6">
              {t('article.1.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.1.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.1.p7')}
            </p>

            <p className="mb-6">
              {t('article.1.p8')}
            </p>

            <div className="bg-red-50 p-6 rounded-lg mt-8 border border-red-100">
              <h3 className="font-bold text-red-900 mb-2 flex items-center">
                <AlertTriangle className="mr-2" />
                {language === 'ko' ? '통행료 인상 압력' : 'Toll Increase Pressure'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-red-800">
                <div>
                  <h4 className="font-semibold mb-2">{language === 'ko' ? '주요 국가 인상률' : 'Major Country Increases'}</h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '체코:' : 'Czechia:'}</strong> {language === 'ko' ? '14.4% 인상' : '14.4% increase'}</li>
                    <li><strong>{language === 'ko' ? '오스트리아:' : 'Austria:'}</strong> {language === 'ko' ? '7.7% 인상' : '7.7% increase'}</li>
                    <li><strong>{language === 'ko' ? '폴란드:' : 'Poland:'}</strong> {language === 'ko' ? '5% 인상 (+40% 추가 예정)' : '5% increase (+40% more expected)'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{language === 'ko' ? '운영 비용 영향' : 'Operating Cost Impact'}</h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '직접 영향:' : 'Direct Impact:'}</strong> {language === 'ko' ? '운영 비용 증가' : 'Higher operating costs'}</li>
                    <li><strong>{language === 'ko' ? '요금 압력:' : 'Rate Pressure:'}</strong> {language === 'ko' ? '지속적인 상승 압력' : 'Sustained upward pressure'}</li>
                    <li><strong>{language === 'ko' ? '2026 전망:' : '2026 Outlook:'}</strong> {language === 'ko' ? '이전 년도보다 가파른 인상' : 'Steeper than previous years'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Ti Insight - European Road Freight 2026 Outlook.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://ti-insight.com/briefs/european-road-freight-2026-outlook/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article1;